import type { EncryptedPayload, SummaryResponse, SummaryStatus, SummaryData } from "~/types/global";

/**
 * Input DTO for generating a new summary
 */
export interface GenerateSummaryInput {
  report_id: string;
  data: any;
  ai_model: string;
  user_prompt?: string;
  report_sources_data?: any[];
  report_sources_used?: any[];
  user_wrapped_dek?: string | null;
  user_dek_iv?: string | null;
  org_wrapped_dek?: string | null;
  key_version?: number | null;
}

/**
 * Input DTO for updating an existing summary
 * Contains mixed plain and encrypted fields:
 * - report_id, summary_status: plain text
 * - report_name, data: encrypted payloads
 * Note: summary_id is optional since backend uses report_id from URL
 */
export interface UpdateSummaryInput {
  report_id?: string;
  summary_id?: string | null;
  report_name?: string | EncryptedPayload;
  summary_status?: SummaryStatus;
  data?: SummaryData | EncryptedPayload | null;
}

/**
 * DTO for handling Summary generation request
 * Validates and transforms the input data before sending to the API
 */
export class GenerateSummaryInputDTO {
  private readonly payload: GenerateSummaryInput;

  constructor(reportId: string, data: any, aiModel: string = "gemini", userPrompt?: string, reportSourcesData?: any[], reportSourcesUsed?: any[], envelope?: {
    user_wrapped_dek?: string | null;
    user_dek_iv?: string | null;
    org_wrapped_dek?: string | null;
    key_version?: number | null;
  }) {
    this.payload = this.validateAndTransform(reportId, data, aiModel, userPrompt, reportSourcesData, reportSourcesUsed, envelope);
  }

  private validateAndTransform(reportId: string, data: any, aiModel: string, userPrompt?: string, reportSourcesData?: any[], reportSourcesUsed?: any[], envelope?: {
    user_wrapped_dek?: string | null;
    user_dek_iv?: string | null;
    org_wrapped_dek?: string | null;
    key_version?: number | null;
  }): GenerateSummaryInput {
    if (!reportId || typeof reportId !== "string" || reportId.trim().length === 0) {
      console.error("Invalid report_id for summary generation");
      throw new Error("ID do relatório inválido para gerar sumário.");
    }

    if (!data) {
      console.error("Missing data for summary generation");
      throw new Error("Dados do relatório são necessários para gerar sumário.");
    }

    const result: GenerateSummaryInput = {
      report_id: reportId,
      data: data,
      ai_model: aiModel,
    };

    result.user_prompt = userPrompt?.trim() ?? "";

    if (reportSourcesData && reportSourcesData.length > 0) {
      result.report_sources_data = reportSourcesData;
    }
    if (reportSourcesUsed && reportSourcesUsed.length > 0) {
      result.report_sources_used = reportSourcesUsed;
    }

    // Só inclui campos de envelope quando a org tem criptografia ativa.
    if (envelope?.org_wrapped_dek) {
      result.user_wrapped_dek = envelope.user_wrapped_dek;
      result.user_dek_iv = envelope.user_dek_iv;
      result.org_wrapped_dek = envelope.org_wrapped_dek;
      result.key_version = envelope.key_version;
    }

    return result;
  }

  /**
   * Get the payload to send to the API
   */
  public getPayload(): GenerateSummaryInput {
    return this.payload;
  }

  /**
   * Get the report ID
   */
  public getReportId(): string {
    return this.payload.report_id;
  }

  /**
   * Get the data
   */
  public getData(): any {
    return this.payload.data;
  }

  /**
   * Convert to JSON for API request
   */
  public toJSON(): GenerateSummaryInput {
    return this.payload;
  }
}

/**
 * DTO for handling Summary update request
 * Validates and transforms the input data before sending to the API
 * Accepts a payload with mixed encrypted and plain fields
 */
export class UpdateSummaryInputDTO {
  private readonly payload: UpdateSummaryInput;

  constructor(summaryPayload: UpdateSummaryInput) {
    this.validateInput(summaryPayload);
    this.payload = summaryPayload;
  }

  private validateInput(payload: UpdateSummaryInput): void {
    // Backend expects summary_status and data in the body
    // report_id is taken from URL path, but we include it for completeness

    // At least one field should be present for update
    if (!payload.summary_status && !payload.data && !payload.report_name) {
      console.error("No fields provided for summary update");
      throw new Error("Nenhum campo fornecido para atualização.");
    }

    // Validate optional fields if present
    if (payload.report_id && (typeof payload.report_id !== "string" || payload.report_id.trim().length === 0)) {
      console.error("Invalid report_id for summary update");
      throw new Error("ID do relatório inválido para atualização.");
    }

    if (payload.summary_id && payload.summary_id !== null && (typeof payload.summary_id !== "string" || payload.summary_id.trim().length === 0)) {
      console.error("Invalid summary_id for summary update");
      throw new Error("ID do sumário inválido para atualização.");
    }

    // Validate encrypted fields if present
    if (payload.report_name && !this.isStringOrEncrypted(payload.report_name)) {
      console.error("Invalid report_name structure for summary update");
      throw new Error("Nome do relatório inválido para atualização.");
    }

    if (payload.data !== null && payload.data !== undefined && !this.isDataOrEncrypted(payload.data)) {
      console.error("Invalid data structure for summary update");
      throw new Error("Dados do sumário inválidos para atualização.");
    }
  }

  private isStringOrEncrypted(value: any): boolean {
    return typeof value === "string" || this.isValidEncryptedPayload(value);
  }

  private isDataOrEncrypted(value: any): boolean {
    // Data can be a SummaryData object or an EncryptedPayload
    if (this.isValidEncryptedPayload(value)) return true;
    if (typeof value === "object" && value !== null && "topics" in value) return true;
    return false;
  }

  private isValidEncryptedPayload(data: any): data is EncryptedPayload {
    return (
      data != null &&
      typeof data === "object" &&
      "encrypted" in data &&
      "iv" in data &&
      typeof data.encrypted === "string" &&
      typeof data.iv === "string" &&
      data.encrypted.length > 0 &&
      data.iv.length > 0
    );
  }

  /**
   * Get the summary ID
   */
  public getSummaryId(): string | null | undefined {
    return this.payload.summary_id;
  }

  /**
   * Get the report ID
   */
  public getReportId(): string | undefined {
    return this.payload.report_id;
  }

  /**
   * Get the full payload
   */
  public getPayload(): UpdateSummaryInput {
    return this.payload;
  }

  /**
   * Convert to JSON for API request
   */
  public toJSON(): UpdateSummaryInput {
    return this.payload;
  }
}
